import React, { useEffect, useMemo, useState, lazy, Component } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { SearchIcon, ChevronRightIcon, AlertCircleIcon, StarIcon, LinkIcon, GitMergeIcon, ArrowRightIcon, ArrowLeftIcon, LibraryIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useDarkMode } from '../context/DarkModeContext';
import { FilterSidebarWrapper, FilterSidebar, FilterSection } from '../components/UI/FilterSidebar';
// --- Mock Data (Same as Series.tsx) ---
const seriesData = [{
  id: 1,
  name: 'Echo Series',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  books: [{
    id: 101,
    title: 'The Silent Echo',
    coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 1
  }, {
    id: 102,
    title: 'Echoes of Darkness',
    coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 2
  }, {
    id: 103,
    title: 'The Final Echo',
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 3
  }],
  totalBooks: 3,
  genres: ['Mystery', 'Thriller'],
  rating: 4.8,
  reviews: 1245,
  status: 'complete',
  completedDate: '2023-08-16',
  popularity: 95,
  connectionType: 'connected',
  endingType: 'HEA',
  contentWarnings: ['Violence', 'Dark Themes']
}, {
  id: 2,
  name: 'Mystical Realms',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  books: [{
    id: 201,
    title: 'Beyond the Veil',
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 1
  }, {
    id: 202,
    title: 'Realm of Shadows',
    coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 2
  }, {
    id: 203,
    title: 'The Crystal Kingdom',
    coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 3
  }, {
    id: 204,
    title: 'The Final Enchantment',
    coverImage: 'https://images.unsplash.com/photo-1603162525937-92a06da4b8ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 4
  }],
  totalBooks: 4,
  genres: ['Fantasy', 'Romance'],
  rating: 4.6,
  reviews: 892,
  status: 'backlist',
  completedDate: '2023-01-10',
  popularity: 88,
  connectionType: 'interconnected',
  endingType: 'HFN',
  spinOffName: 'Crystal Chronicles',
  spinOffId: '99'
}, {
  id: 3,
  name: 'Midnight Chronicles',
  author: 'Sophia Williams',
  coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  books: [{
    id: 301,
    title: 'Midnight Shadows',
    coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 1
  }, {
    id: 302,
    title: 'Midnight Whispers',
    coverImage: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 2
  }, {
    id: 303,
    title: 'Midnight Secrets',
    coverImage: 'https://images.unsplash.com/photo-1629992101753-56d196c8aabb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 3
  }],
  totalBooks: 5,
  genres: ['Horror', 'Supernatural'],
  rating: 4.7,
  reviews: 756,
  status: 'ongoing',
  popularity: 82,
  connectionType: 'connected',
  contentWarnings: ['Horror', 'Gore'],
  preorderBook: {
    number: 4,
    id: 'midnight-4'
  }
}, {
  id: 4,
  name: 'The Explorer Chronicles',
  author: 'David Miller',
  coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  books: [{
    id: 401,
    title: 'The Lost City',
    coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 1
  }, {
    id: 402,
    title: 'The Ancient Temple',
    coverImage: 'https://images.unsplash.com/photo-1580934738404-5eb8d4c0389e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 2
  }],
  totalBooks: 4,
  genres: ['Adventure', 'Historical Fiction'],
  rating: 4.5,
  reviews: 623,
  status: 'hiatus',
  popularity: 78,
  connectionType: 'connected'
}, {
  id: 5,
  name: 'Quantum Trilogy',
  author: 'Alex Zhang',
  coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  books: [{
    id: 501,
    title: 'Quantum Dreams',
    coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 1
  }, {
    id: 502,
    title: 'Quantum Reality',
    coverImage: 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    position: 2
  }],
  totalBooks: 3,
  genres: ['Science Fiction', 'Thriller'],
  rating: 4.4,
  reviews: 489,
  status: 'ongoing',
  popularity: 75,
  connectionType: 'interconnected',
  preorderBook: {
    number: 3,
    id: 'quantum-3'
  }
}];
// --- Helpers ---
const getGenreColor = (genre: string): string => {
  const genreLower = genre.toLowerCase();
  if (genreLower.includes('romance')) return 'bg-pink-600';
  if (genreLower.includes('fantasy')) return 'bg-indigo-600';
  if (genreLower.includes('sci-fi') || genreLower.includes('science')) return 'bg-blue-600';
  if (genreLower.includes('mystery')) return 'bg-indigo-600';
  if (genreLower.includes('thriller')) return 'bg-red-600';
  if (genreLower.includes('horror')) return 'bg-gray-800';
  if (genreLower.includes('historical')) return 'bg-amber-600';
  if (genreLower.includes('young adult') || genreLower.includes('ya')) return 'bg-cyan-600';
  if (genreLower.includes('contemporary')) return 'bg-emerald-600';
  return 'bg-violet-600';
};
const getBookCountBadgeColor = (status: string, hasPreorder?: boolean): string => {
  const s = status?.toLowerCase() || '';
  if (hasPreorder && s === 'ongoing') return 'bg-amber-500/80';
  if (s === 'complete' || s === 'backlist' || s === 'spin_off_active') return 'bg-green-600/80';
  if (s === 'ongoing') return 'bg-blue-500/80';
  if (s.includes('hiatus')) return 'bg-amber-500/80';
  if (s.includes('cancel') || s.includes('discontinu')) return 'bg-red-600/80';
  return 'bg-indigo-500/80';
};
// Status color — matches UnifiedBookCard's getStatusColor exactly
const getSeriesStatusColor = (status: string): string => {
  const s = status?.toLowerCase() || '';
  if (s === 'complete' || s === 'backlist') return 'bg-green-600';
  if (s === 'ongoing') return 'bg-yellow-500';
  if (s.includes('hiatus') || s.includes('cancel') || s.includes('discontinu') || s.includes('stopped') || s.includes('removed')) return 'bg-red-600';
  return 'bg-blue-600';
};
// Status label
const getSeriesStatusLabel = (status: string): string => {
  const s = status?.toLowerCase() || '';
  if (s === 'complete' || s === 'backlist') return 'Complete';
  if (s === 'ongoing') return 'Ongoing';
  if (s.includes('hiatus')) return 'Hiatus';
  if (s.includes('cancel') || s.includes('discontinu')) return 'Discontinued';
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
};
// --- Card Component ---
const SeriesGridCard = ({
  series,
  darkMode
}: {
  series: any;
  darkMode: boolean;
}) => {
  const [imgError, setImgError] = useState(false);
  const coverImage = series.coverImage || series.books && series.books[0]?.coverImage || '';
  const showEndingType = (series.status === 'complete' || series.status === 'backlist') && series.endingType;
  const showPreOrder = series.status === 'ongoing' && series.preorderBook && !showEndingType;
  const publishedCount = series.books?.length || 0;
  const totalCount = series.totalBooks || publishedCount;
  const bookCountLabel = totalCount > 0 ? `${publishedCount}/${totalCount}` : `${publishedCount}`;
  const bookCountColor = getBookCountBadgeColor(series.status, !!series.preorderBook);
  const authorSlug = series.author?.toLowerCase().replace(/\s+/g, '-') || '';
  return <div className="group/card relative w-full" data-id="element-13532" data-mp="3dtd422znotax">
      <Link to={`/series/${series.id}`} className="block relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover/card:scale-105" data-id="element-13533" data-mp="3ds2125e1bpkv">
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-13534" data-mp="2q4bta93cp62h">
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-3" data-id="element-13535" data-mp="2uwv7n89v458c">
              {series.name}
            </span>
          </div> : <img src={coverImage} alt={series.name} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-13536" data-mp="22dybdbehcnlm" />}

        {/* ALWAYS VISIBLE ON COVER */}
        {series.genres && series.genres[0] && <span className={`absolute top-2 left-2 text-white text-xs font-medium ${getGenreColor(series.genres[0])} px-2 py-0.5 rounded-full shadow-sm z-10`} data-id="element-13537" data-mp="24s2hsg13nu1r">
            {series.genres[0]}
          </span>}

        {/* Top Right: Status Badge — inline span matching UnifiedBookCard style */}
        {series.status && <span className={`absolute top-2 right-2 text-white text-xs ${getSeriesStatusColor(series.status)} px-2 py-0.5 rounded-full z-10`} data-id="element-13538" data-mp="1m1bb5m8hyhr2">
            {getSeriesStatusLabel(series.status)}
          </span>}

        {series.contentWarnings && series.contentWarnings.length > 0 && <div className="absolute bottom-2 left-2 z-10" title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-13539" data-mp="yap33uyhsxh2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow-md" data-id="element-13540" data-mp="qpljbhcccp26">
              <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-13541" data-mp="gt8qelomef8h" />
            </span>
          </div>}

        {/* HOVER OVERLAY */}
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover/card:opacity-100 flex flex-col p-3 transition-opacity duration-200 text-white z-20" data-id="element-13542" data-mp="btvuzcjonzua">
          <div className="min-w-0" data-id="element-13543" data-mp="323m36ab4yrt">
            <div className="flex items-start justify-between gap-1 mb-0.5" data-id="element-13544" data-mp="312b11takca3">
              <Link to={`/series/${series.id}`} className="font-bold text-white text-sm line-clamp-1 hover:text-indigo-300 transition-colors flex-1 min-w-0" onClick={e => e.stopPropagation()} data-id="element-13545" data-mp="3u9g6du6hlsby">
                {series.name}
              </Link>
              <span className={`flex-shrink-0 text-[10px] font-bold text-white px-1.5 py-0.5 rounded ${bookCountColor}`} data-id="element-13546" data-mp="3g9f5wfu4wmx0">
                {bookCountLabel}
              </span>
            </div>

            <p className="text-white/80 text-xs line-clamp-1 mb-2" data-id="element-13547" data-mp="3aks4ols8eoa7">
              by{' '}
              <Link to={`/author/${authorSlug}`} className="hover:text-indigo-300 hover:underline transition-colors" onClick={e => e.stopPropagation()} data-id="element-13548" data-mp="2woy8928n9rdz">
                {series.author}
              </Link>
            </p>

            {series.connectionType && <div className="mb-2" data-id="element-13549" data-mp="3flzf6slre1kk">
                <span className={`inline-flex items-center ${rt.badgeRadius} border px-1.5 py-0.5 text-[10px] font-medium ${series.connectionType === 'connected' ? 'bg-teal-900/40 text-teal-200 border-teal-800' : 'bg-slate-800/60 text-slate-300 border-slate-700'}`} data-id="element-13550" data-mp="3fkwp2mw9g35p">
                  {series.connectionType === 'connected' ? <LinkIcon className="h-2.5 w-2.5 mr-1" data-id="element-13551" data-mp="qaj5xgiolk62" /> : <GitMergeIcon className="h-2.5 w-2.5 mr-1" data-id="element-13552" data-mp="m95xcyo7f2vg" />}
                  {series.connectionType === 'connected' ? 'Connected' : 'Interconnected'}
                </span>
              </div>}

            <div className="flex flex-wrap gap-1 mb-2" data-id="element-13553" data-mp="v29izu906m16">
              {showEndingType && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-600/80 text-white" data-id="element-13554" data-mp="7cf5dagv0tfv">
                  {series.endingType}
                </span>}
              {showPreOrder && <Link to={`/book/${series.preorderBook.id}`} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/80 text-white hover:bg-amber-500 transition-colors" onClick={e => e.stopPropagation()} data-id="element-13555" data-mp="h803uympzbkv">
                  Pre-Order Bk {series.preorderBook.number}
                </Link>}
              {series.spinOffName && series.spinOffId && <Link to={`/series/${series.spinOffId}`} className="px-1.5 py-0.5 rounded text-[10px] font-medium text-fuchsia-300 bg-fuchsia-900/40 border border-fuchsia-800 hover:text-fuchsia-200 transition-colors flex items-center" onClick={e => e.stopPropagation()} data-id="element-13556" data-mp="27cswff3jjk8">
                  {series.spinOffName}{' '}
                  <ArrowRightIcon className="h-2.5 w-2.5 ml-0.5" data-id="element-13557" data-mp="l6gzzin2v399" />
                </Link>}
              {series.contentWarnings && series.contentWarnings.length > 0 && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/30 text-amber-200" title={`Content Warnings: ${series.contentWarnings.join(', ')}`} data-id="element-13558" data-mp="uwjyfycyx9ez">
                  <AlertCircleIcon className="h-3 w-3" data-id="element-13559" data-mp="13y1bt8yzqwzz" />
                </span>}
            </div>
          </div>

          <div className="mt-auto flex items-center justify-between pt-2 border-t border-white/10" data-id="element-13560" data-mp="2od653k419h4w">
            <div className="flex items-center text-amber-400" data-id="element-13561" data-mp="2jisio77cmkcd">
              <StarIcon className="h-3.5 w-3.5 fill-current" data-id="element-13562" data-mp="2ffnhc7z08opb" />
              <span className="ml-1 text-xs font-bold text-white" data-id="element-13563" data-mp="2agauvoc1pjn9">
                {series.rating}
              </span>
              <span className="ml-1 text-[10px] text-white/60" data-id="element-13564" data-mp="374yxpdf6x0c3">
                ({series.reviews})
              </span>
            </div>
            <span className="text-[10px] font-medium text-white/70 group-hover/card:text-white transition-colors flex items-center" data-id="element-13565" data-mp="336jg5p1vok62">
              View Series <ArrowRightIcon className="h-3 w-3 ml-0.5" data-id="element-13566" data-mp="2tf5b71r5qs5j" />
            </span>
          </div>
        </div>
      </Link>
    </div>;
};
// --- Main Page Component ---
const SeriesAll = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    darkMode
  } = useDarkMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Filter States
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string[]>([]);
  // Sync URL params to state on mount/update
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const genreParam = searchParams.get('genre');
    if (statusParam) {
      setSelectedStatus([statusParam]);
    } else {
      setSelectedStatus([]);
    }
    if (genreParam) {
      setSelectedGenres([genreParam]);
    } else {
      setSelectedGenres([]);
    }
  }, [searchParams]);
  // Filter Logic
  const filteredSeries = useMemo(() => {
    return seriesData.filter(series => {
      // Text search
      const matchesSearch = series.name.toLowerCase().includes(searchTerm.toLowerCase()) || series.author.toLowerCase().includes(searchTerm.toLowerCase());
      // Status filter
      let matchesStatus = true;
      if (selectedStatus.length > 0) {
        matchesStatus = selectedStatus.includes(series.status);
        // Handle 'complete' mapping to 'backlist' if needed, or treat them distinct
        // For this mock, we'll match exact status strings
        if (selectedStatus.includes('complete')) {
          matchesStatus = matchesStatus || series.status === 'complete' || series.status === 'backlist';
        }
      }
      // Genre filter
      let matchesGenre = true;
      if (selectedGenres.length > 0) {
        matchesGenre = selectedGenres.some(g => series.genres.some(sg => sg.toLowerCase() === g.toLowerCase()));
      }
      // Connection filter
      let matchesConnection = true;
      if (selectedConnection.length > 0) {
        matchesConnection = series.connectionType && selectedConnection.includes(series.connectionType);
      }
      return matchesSearch && matchesStatus && matchesGenre && matchesConnection;
    });
  }, [searchTerm, selectedStatus, selectedGenres, selectedConnection, seriesData]);
  // Sidebar Sections
  const filterSections: FilterSection[] = [{
    id: 'status',
    title: 'Status',
    type: 'checkbox',
    options: [{
      value: 'complete',
      label: 'Complete'
    }, {
      value: 'ongoing',
      label: 'Ongoing'
    }, {
      value: 'backlist',
      label: 'Backlist'
    }, {
      value: 'hiatus',
      label: 'Hiatus'
    }],
    value: selectedStatus,
    onChange: val => {
      setSelectedStatus(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
    }
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'fantasy',
      label: 'Fantasy'
    }, {
      value: 'romance',
      label: 'Romance'
    }, {
      value: 'thriller',
      label: 'Thriller'
    }, {
      value: 'mystery',
      label: 'Mystery'
    }, {
      value: 'sci-fi',
      label: 'Sci-Fi'
    }, {
      value: 'horror',
      label: 'Horror'
    }, {
      value: 'adventure',
      label: 'Adventure'
    }],
    value: selectedGenres,
    onChange: val => {
      setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
    }
  }, {
    id: 'connection',
    title: 'Connection Type',
    type: 'checkbox',
    options: [{
      value: 'connected',
      label: 'Connected'
    }, {
      value: 'interconnected',
      label: 'Interconnected'
    }],
    value: selectedConnection,
    onChange: val => {
      setSelectedConnection(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
    }
  }];
  const handleReset = () => {
    setSelectedStatus([]);
    setSelectedGenres([]);
    setSelectedConnection([]);
    setSearchTerm('');
    setSearchParams({});
  };
  return <div className={`min-h-screen flex flex-col ${dk.page}`} data-id="element-13567" data-mp="15ix1gazfswit">
      {/* Header */}
      <div className={`py-6 px-4 border-b ${dk.surface} ${dk.border}`} data-id="element-13568" data-mp="10m6j1h05tou8">
        <div className="max-w-7xl mx-auto w-full" data-id="element-13569" data-mp="wkbhqci8bbgg">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-13570" data-mp="wlmq6pmmd7dw">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-13571" data-mp="2uy4qpeitmco0">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-13572" data-mp="cqldad946tta" />
            <Link to="/browse" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-13573" data-mp="cptjn7z2v65n">
              Books
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-13574" data-mp="mgxwa11a6exb" />
            <Link to="/series" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-13575" data-mp="qhgo3v36bkzi">
              Series Discovery
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-13576" data-mp="3pyohs2l02v9z" />
            <span className={dk.textPrimary} data-id="element-13577" data-mp="3uwpyiguyeiia">All Series</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-13578" data-mp="2re1hui1378a">
            <div className="flex items-center gap-4" data-id="element-13579" data-mp="7qqjo1xyfq4j">
              <Link to="/series" className={`p-2 rounded-full border ${dk.border} hover:${dk.surfaceMuted} transition-colors`} aria-label="Back to Series Discovery" data-id="element-13580" data-mp="7sa5e82dqtdp">
                <ArrowLeftIcon className={`h-5 w-5 ${dk.textSecondary}`} data-id="element-13581" data-mp="3f9a7ok9d2w95" />
              </Link>
              <div data-id="element-13582" data-mp="3b7p11gnf7e7e">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-13583" data-mp="3b888g3fg2k76">
                  Browse All Series
                </h1>
                <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-13584" data-mp="3k8dfgcgq7gkt">
                  {filteredSeries.length} series found
                </p>
              </div>
            </div>

            <div className="relative w-full md:w-80" data-id="element-13585" data-mp="2wgpueb65azqg">
              <input type="text" placeholder="Search series or authors..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full px-5 py-2.5 pl-10 rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-13586" data-mp="2rkhakkh06icy" />
              <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-13587" data-mp="2dpygqxf4kn69" />
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-1 max-w-7xl mx-auto w-full" data-id="element-13588" data-mp="t25q5bbbziv9">
        {/* Sidebar */}
        <FilterSidebarWrapper isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-13589" data-mp="far38teayxix">
          <FilterSidebar sections={filterSections} onReset={handleReset} searchable={false} data-id="element-13590" data-mp="1726ook44ie7k" />
        </FilterSidebarWrapper>

        {/* Main Grid */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto" data-id="element-13591" data-mp="y0gytipw3nbw">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4" data-id="element-13592" data-mp="12y8qqcqjb9g3">
            <button onClick={() => setIsSidebarOpen(true)} className={`flex items-center px-4 py-2 rounded-lg border ${dk.border} ${dk.surface} ${dk.textPrimary}`} data-id="element-13593" data-mp="3irhfvr4e48tl">
              <LibraryIcon className="h-4 w-4 mr-2" data-id="element-13594" data-mp="6v3blz82xg87" />
              Filters
            </button>
          </div>

          {filteredSeries.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6" data-id="element-13595" data-mp="21qm7mvkt5ee">
              {filteredSeries.map(series => <SeriesGridCard key={series.id} series={series} darkMode={darkMode} data-id="element-13596" data-mp="3p9kj98cgpbsj" />)}
            </div> : <div className="text-center py-20" data-id="element-13597" data-mp="kvmfcu5ii04k">
              <p className={`text-lg ${dk.textSecondary}`} data-id="element-13598" data-mp="gspranx7dbt8">
                No series found matching your filters.
              </p>
              <button onClick={handleReset} className="mt-4 text-indigo-600 hover:text-indigo-500 font-medium" data-id="element-13599" data-mp="bufts6o09z4g">
                Clear all filters
              </button>
            </div>}
        </main>
      </div>
    </div>;
};
export default SeriesAll;